import React, { useCallback, useEffect, useState } from "react"
import { FC } from "react"
import { CollectionUtil, StringUtil, ValidateUtil } from "src/framework/commons"
import { FullWidthButton } from "src/framework/components/buttons"
import { InputText } from "src/framework/components/forms"
import { InputPhone } from "src/framework/components/forms/inputs/InputPhone"
import { CEPMask } from "src/framework/components/forms/masks/CEPMask"
import { IDataResponse, STATUS_CODE } from "src/framework/providers/api"
import { notifierStore } from "src/framework/store/NotifierStore"
import { CepsProvider } from "src/providers/ceps/CepsProvider"
import { PedidosProvider } from "src/providers/pagamentos/PedidoProvider"
import { getCidades, getEstados } from "src/store/global.store"
import { PedidoStore } from "src/store/vendas/PedidoStore"
import { Cidade, EnderecosClientes, ErrorValidate, Estado, Paises, PedidoType } from "src/types"

export interface EnderecoFormProperties {
    onSave?: Function
    onCancel?: Function
    edit?: boolean,
    paisDefault?: Paises,
    paises?: any,
    endereco?: EnderecosClientes,
    tipoEndereco: string,
    clean?: boolean,
}

interface ErrorState {
    cep: ErrorValidate
    pais: ErrorValidate
    logradouro: ErrorValidate
    numero: ErrorValidate
    telefone: ErrorValidate
    bairro: ErrorValidate
    estado: ErrorValidate
    cidade: ErrorValidate
    complemento: ErrorValidate
    descricao: ErrorValidate
    contato: ErrorValidate
}

const INITIAL_ERROR_STATE: ErrorState = {
    cep: {
        hasError: false,
        hintText: ""
    },
    pais: {
        hasError: false,
        hintText: ""
    },
    logradouro: {
        hasError: false,
        hintText: ""
    },
    telefone: {
        hasError: false,
        hintText: ""
    },
    numero: {
        hasError: false,
        hintText: ""
    },
    bairro: {
        hasError: false,
        hintText: ""
    },
    estado: {
        hasError: false,
        hintText: ""
    },
    cidade: {
        hasError: false,
        hintText: ""
    },
    complemento: {
        hasError: false,
        hintText: ""
    },
    descricao: {
        hasError: false,
        hintText: ""
    },
    contato: {
        hasError: false,
        hintText: ""
    },
}

const hasErrors = (errors: ErrorState): boolean => {
    const errorKeys = Object.keys(errors);
    for (const errorKey of errorKeys) {
        if ( (errors as any)[errorKey].hasError ) {
            return true; 
        }
    }
    return false;
}

const pedidosProvider = new PedidosProvider();

const FromEndereco: FC<EnderecoFormProperties> = ({
    onSave = () => { },
    onCancel = () => { },
    edit,
    paisDefault,
    paises,
    endereco,
    tipoEndereco,
    clean
}) => {

    const [paisState, setPaisState] = React.useState<Paises>();
    const [estado, setEstado] = React.useState<any>();
    const [enderecoResult, setEnderecoResult] = React.useState<EnderecosClientes | null>(null);
    const [cidadeListState, setCidadeListState] = useState<any[]>();
    const [estadoListState, setEstadoListState] = useState<any[]>();

    const [errorState, setErrorState] = React.useState<ErrorState>(INITIAL_ERROR_STATE);

    const cepProvider = new CepsProvider();

    const cidades = async () => {
        const cidades = await getCidades();
        if (cidades) {
            setCidadeListState(cidades);
        }

        const estados = await getEstados();
        if (estados) {
            setEstadoListState(estados);
        }
    }

    
    useEffect(() => {

        cidades();

        if (endereco) {
            setEnderecoResult(endereco);
            setEstado(endereco.cidade?.estado_id);

            if (edit && endereco.tipo)
                tipoEndereco = endereco.tipo;
        }

        if (paisDefault) {
            setPaisState(paisDefault);
        }
    }, [])

    useEffect(() => {

        if (paisDefault) {
            setPaisState({
                id: paisDefault.id,
                nome: paisDefault.nome
            });
        }
    }, [paisDefault])

    useEffect(() => {
        setPaisState(paises?.find((pais: Paises) => pais.nome === 'BRASIL'));
    }, [paises])

    useEffect(() => {
        if(!edit) {
            setEnderecoResult(null);
            setEstado(null);
        }
    }, [clean]);

    const parseSaveData = (data: any): any => {
        if (data.logradouro){
            data.logradouro = data.logradouro.replace(/[^a-zA-Z0-9-' ]/g,"");
        }
        if (data.bairro){
            data.bairro = data.bairro.replace(/[^a-zA-Z0-9-' ]/g,"");
        }
        if (data.complemento){
            data.complemento = data.complemento.replace(/[^a-zA-Z0-9-' ]/g,"");
        }
        if (data.nome){
            data.nome = data.nome.replace(/[^a-zA-Z0-9-' ]/g,"");
        }        
        return data;
    }

    const validateEndereco = (): boolean => {
        const errorMessages: string[] = []
        const errors = { ...errorState }

        if (!enderecoResult?.cep || enderecoResult?.cep === "") {
            errorMessages.push(`O campo "Cep" é obrigatório.`)
            errors.cep = { hasError: true, hintText: "" }
        }

        if (!enderecoResult?.logradouro || enderecoResult?.logradouro === "") {
            errorMessages.push(`O campo "Logradouro" é obrigatório.`)
            errors.logradouro = { hasError: true, hintText: "" }
        }

        if (!enderecoResult?.bairro || enderecoResult?.bairro === "") {
            errorMessages.push(`O campo "Bairro" é obrigatório.`)
            errors.bairro = { hasError: true, hintText: "" }
        }

        if (!enderecoResult) {
            errorMessages.push(`O campo "Estado" é obrigatório.`)
            errors.estado = { hasError: true, hintText: "" }
        }

        if (!enderecoResult?.cidade || enderecoResult?.cidade.id === null) {
            errorMessages.push(`O campo "Cidade" é obrigatório.`)
            errors.cidade = { hasError: true, hintText: "" }
        }

        if (!enderecoResult?.numero || enderecoResult?.numero.trim() === "") {
            errorMessages.push(`O campo "Numero" é obrigatório.`)
            errors.numero = { hasError: true, hintText: "" }
        }

        if (!paisState || paisState.id === null) {
            errorMessages.push(`O campo "País" é obrigatório.`)
            errors.pais = { hasError: true, hintText: "" }
        }

        if (!enderecoResult?.descricao || enderecoResult?.descricao === "") {
            errorMessages.push(`O campo "Descrição Endereço" é obrigatório.`)
            errors.descricao = { hasError: true, hintText: "" }
        }

        if (!enderecoResult?.telefone || enderecoResult?.telefone === "") {
            errorMessages.push(`O campo "Contato do responsável pelo recebimento" é obrigatório.`)
            errors.telefone = { hasError: true, hintText: "" }
        }

        if (!enderecoResult?.nome || enderecoResult?.nome === "") {
            errorMessages.push(`O campo "Responsável pelo Recebimento" é obrigatório.`)
            errors.contato = { hasError: true, hintText: "" }
        }

        if (errorMessages.length > 0) {
            setErrorState({ ...errors })
            notifierStore.setNotifier({
                title: "Aviso",
                content: errorMessages,
                timeOut: 3500,
                onClose: () => {
                    notifierStore.setOpen(false)
                }
            })
            notifierStore.setOpen(true)

            return false
        }

        return true;
    }
    const maskEspeciais = useCallback((str: any)=>{
        if(str){
            let _str = str;
            _str = _str.replace(/[^a-zA-Z0-9-' ]/g,"");
            return _str;
        }
        return null;
    },[enderecoResult])

    return <>
        <div className="row">
            {/*
            <div className="X12 M6">
                <InputComboBoxList
                    id={`listagem-pais-cb`}
                    key={`listagem-pais-cb`}
                    label={"País"}
                    textField="nome"
                    valueField="id"
                    options={paises || []}
                    value={paisState}
                    autoComplete
                    error={errorState.pais.hasError}
                    disabled={edit ? true : false}
                    renderOptions={(option: any) => {
                        return `${option.nome}`
                    }}
                    renderOptionsLabel={(option: any) => {
                        return `${option.nome}`
                    }}
                    onChange={(value: Paises) => {
                        if (value) {
                            setPaisState(value);
                            const estadoState = { ...enderecoResult }; 
                            estadoState.pais_id = value.id
                            setEnderecoResult(estadoState);
                        }
                    }}
                />
            </div>
                */}
            <div className="X12 M12">
                <InputText label="CEP *"
                    fullWidth
                    InputProps={{
                        inputComponent: CEPMask
                    }}
                    value={enderecoResult?.cep || null}
                    onChange={async (e: any) => {
                        const enderecoState = { ...enderecoResult }

                        if (e) {
                            const value = e.target.value?.replace(/\D/g, "") || '';
                            if (value.length < 8) {
                                enderecoState.bairro = "";
                                enderecoState.cidade = undefined;
                                enderecoState.cidade_id = undefined;
                                enderecoState.logradouro = "";
                                enderecoState.numero = "";
                                enderecoState.complemento = "";
                                setEstado("");
                            }

                            if (value.length > 7) {
                                const pedido = PedidoStore.getPedido();
                                if (pedido?.codigo_venda) {
                                    const pedidosProvider = new PedidosProvider();
                                    const responsePedido: IDataResponse<PedidoType[]> = await pedidosProvider.getPedidos(pedido.codigo_venda);
                                    if (responsePedido.statusCode !== STATUS_CODE.OK) return;
                                }

                                cepProvider.getCep(value).then((response: any) => {
                                    if (response.data && response.statusCode === 200) {
                                        enderecoState.cep = value;
                                        enderecoState.logradouro = response.data.endereco.normalize('NFD').replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '').substring(0, 30);
                                        enderecoState.bairro = response.data.bairro.normalize('NFD').replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '').substring(0, 30);
                                        enderecoState.pais = paisState;

                                        if (paisState?.nome === "BRASIL") {
                                            enderecoState.cidade = cidadeListState?.find((c: Cidade) => c.id === response.data.ibge);
                                            setEstado(response.data.uf);
                                        } else {
                                            const cidadeResult: Cidade = cidadeListState?.find((c: Cidade) => c.nome === 'Exterior');
                                            enderecoState.cidade = cidadeResult;
                                            setEstado(cidadeResult.estado_id);
                                        }

                                        setEnderecoResult(enderecoState);
                                    } else {
                                        enderecoState.cep = "";

                                        const errorMessages: string[] = []
                                        const errors = { ...errorState }

                                        errorMessages.push("CEP não encontrado!");

                                        setErrorState({ ...errors })
                                        notifierStore.setNotifier({
                                            title: "Aviso",
                                            content: errorMessages,
                                            timeOut: 3500,
                                            onClose: () => {
                                                notifierStore.setOpen(false)
                                            }
                                        })
                                        notifierStore.setOpen(true)
                                    }
                                });
                            } else {
                                enderecoState.cep = value;
                                setEnderecoResult(enderecoState);
                            }
                        } else {
                            enderecoState.cep = "";
                            setEnderecoResult(enderecoState);
                        }
                    }}
                    error={errorState.cep.hasError}
                    helperText={errorState.cep.hasError ? errorState.cep.hintText : ""}
                />
            </div>

            <div className="X12">
                <InputText label="Logradouro *"
                    fullWidth
                    value={enderecoResult?.logradouro ? enderecoResult?.logradouro?.normalize('NFD').replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '') : ""}
                    maxLength={30}
                    onChange={(e: any) => {
                        if (e) {
                            const state = { ...enderecoResult };
                            state.logradouro = e.target.value;
                            setEnderecoResult(state);
                        }
                    }}
                    error={errorState.logradouro.hasError}
                    helperText={errorState.logradouro.hasError ? errorState.logradouro.hintText : ""}
                />
            </div>

            <div className="X12 M9">
                <InputText label="Bairro *"
                    fullWidth
                    value={enderecoResult?.bairro?.normalize('NFD').replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '')  || ""}
                    maxLength={30}
                    error={errorState.bairro.hasError}
                    onChange={(e: any) => {
                        if (e) {
                            const value = { ...enderecoResult };
                            value.bairro = e.target.value;
                            setEnderecoResult(value);
                        }
                    }}
                />
            </div>

            <div className="X12 M3">
                <InputText 
                    label="Número *"
                    fullWidth
                    value={enderecoResult?.numero  || ""}
                    onChange={(e: any) => {
                        if (e) {
                            const value = { ...enderecoResult };
                            value.numero = e.target?.value?.trim()?.replace(/[^0-9]/g, "");
                            setEnderecoResult(value);
                        }
                    }}
                    error={errorState.numero.hasError}
                    helperText={errorState.numero.hasError ? errorState.numero.hintText : ""}
                />
            </div>

            <div className="X12 M8">
                <InputText
                    fullWidth
                    label="Cidade *"
                    value={enderecoResult?.cidade?.nome}
                    disabled={true}
                    error={errorState.cidade.hasError}
                    helperText={errorState.cidade.hasError ? errorState.cidade.hintText : ""}
                />
            </div>

            <div className="X12 M4">
                <InputText
                    fullWidth
                    label={"Estado *"}
                    value={estadoListState?.find((e: Estado) => e.id === estado)?.nome || ""}
                    disabled={true}
                />
            </div>

            <div className="X12">
                <InputText label="Complemento"
                    fullWidth
                    value={enderecoResult?.complemento || ""}
                    error={errorState.complemento.hasError}
                    maxLength={15}
                    onChange={(e: any) => {
                        if (e) {
                            const state = { ...enderecoResult };
                            const valor = e.target.value;
                            state.complemento = valor.replace(/[^a-zA-Z0-9 ]/g, "").slice(0, 15) || null;
                            setEnderecoResult(state);
                        }
                    }}
                    onBlur={()=>{
                        //faz o trim() somente quando sai do campo
                        const state = { ...enderecoResult };
                        if(state.complemento){
                            state.complemento = state.complemento.trim();
                            setEnderecoResult(state);
                        }
                    }}
                />
            </div>

            <div className="X12 M6">
                <InputText label="Responsável pelo Recebimento *"
                    fullWidth
                    value={maskEspeciais(enderecoResult?.nome) || ""}
                    error={errorState.contato.hasError}
                    onChange={(e: any) => {
                        if (e) {
                            const value = { ...enderecoResult };
                            value.nome = e.target.value;
                            setEnderecoResult(value);
                        }
                    }}
                />
            </div>

            <div className="X12 M6">
                    <InputPhone label="Contato do responsável pelo recebimento *"
                        fullWidth
                        value={enderecoResult?.telefone  || undefined}
                        error={errorState.telefone.hasError}
                        onChangeValue={(newValue) => {
                            const value = { ...enderecoResult };
                            value.telefone = newValue.numberValue?.toString();
                            setEnderecoResult(value);
                        }}
                        onBlur={(event: any) => {
                            if (!ValidateUtil.isValidPhone(event.target.value)) {
                                const _state = { ...enderecoResult };
                                _state.telefone = '';
                                setEnderecoResult(_state);
                            }
                        }}
                    />
                </div>

            <div className="X12">
                <InputText label="Descrição Endereço *"
                    fullWidth
                    value={enderecoResult?.descricao  || ""}
                    error={errorState.descricao.hasError}
                    onChange={(e: any) => {
                        if (e) {
                            const value = { ...enderecoResult };
                            value.descricao = e.target.value;
                            setEnderecoResult(value);
                        }
                    }}
                />
            </div>
            <div className="X12 M6">
                <FullWidthButton
                    text={"Cancelar"}
                    color={"secondary"}
                    onClick={() => {
                        if (onCancel) {
                            onCancel();
                        }
                    }}
                />
            </div>
            <div className="X12 M6">
                <FullWidthButton
                    disableElevation={true}
                    text={edit ? "Salvar" : "Adicionar"}
                    color={"primary"}
                    variant={"contained"}
                    onClick={async () => {
                        const pedido = PedidoStore.getPedido();
                        if (pedido?.codigo_venda) {
                            const substituicaoResult = await pedidosProvider.verificarSubstituicao(pedido.codigo_venda);
                            if (substituicaoResult.statusCode !== STATUS_CODE.OK) return;
                        }

                        if (!validateEndereco()) {
                            return
                        }
                        
                        const _data = parseSaveData(enderecoResult);                        
                        const enderecoEnv = {
                            ..._data,
                            cidade_id: enderecoResult?.cidade?.id,
                            pais_id: paisState?.id,
                            tipo: tipoEndereco
                        }

                        onSave(enderecoEnv);
                    }}
                />
            </div>
        </div>
    </>
}

export { FromEndereco }